import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle, XCircle, Pause, StopCircle, Play } from "@phosphor-icons/react";

/**
 * UAT Batch 1 — Candidate Service Status change dialog.
 * Allowed statuses: Active, Service Completed, Service Terminated,
 * Service Discontinued, On Hold. Captures effective_date, reason and
 * remarks (remarks optional).
 *
 * Authorization is enforced server-side (Admin, HR, Manager, Group Lead).
 * The button that OPENS this modal in CandidateDetail is also gated.
 */
const STATUS_OPTIONS = [
  { key: "Active",                 label: "Active / Resume",         icon: Play,        tone: "text-emerald-700 border-emerald-200 bg-emerald-50" },
  { key: "On Hold",                label: "On Hold",                 icon: Pause,       tone: "text-amber-700 border-amber-200 bg-amber-50" },
  { key: "Service Completed",      label: "Service Completed",      icon: CheckCircle, tone: "text-sky-700 border-sky-200 bg-sky-50" },
  { key: "Service Terminated",     label: "Service Terminated",     icon: XCircle,     tone: "text-red-700 border-red-200 bg-red-50" },
  { key: "Service Discontinued",   label: "Service Discontinued",   icon: StopCircle,  tone: "text-slate-700 border-slate-200 bg-slate-50" },
];

const CLOSED = new Set(["Service Completed", "Service Terminated", "Service Discontinued"]);

export default function CandidateServiceStatusModal({ candidate, onClose, onChanged }) {
  const currentStatus = candidate?.service_status || "Active";
  const [status, setStatus] = useState(currentStatus);
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (status === currentStatus) {
      toast.error("Please pick a different status");
      return;
    }
    if (CLOSED.has(status) && !reason.trim()) {
      toast.error("Reason is required when closing a service");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/candidates/${candidate.id}/service-status`, {
        status,
        effective_date: effectiveDate,
        reason: reason.trim() || null,
        remarks: remarks.trim() || null,
      });
      toast.success(`Service status → ${status}`);
      onChanged && onChanged();
      onClose();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to change status");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center p-4 z-50"
         data-testid="candidate-service-status-modal">
      <div className="bg-white border border-slate-200 rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-6 pb-3 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-semibold text-lg">Change Service Status</h3>
            <p className="text-xs text-slate-500">
              {candidate.full_name} · currently <b>{currentStatus}</b>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">Close</button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">New Status</div>
            <div className="space-y-2">
              {STATUS_OPTIONS.map((opt) => {
                const active = status === opt.key;
                return (
                  <label key={opt.key} data-testid={`svc-status-${opt.key.toLowerCase().replace(/\s+/g, "-")}`}
                         className={`flex items-start gap-3 border rounded-md px-3 py-2 cursor-pointer transition ${active ? opt.tone + " ring-2 ring-offset-1 ring-current" : "border-slate-200 hover:bg-slate-50"}`}>
                    <input type="radio" name="svcstatus" value={opt.key}
                           checked={active}
                           onChange={() => setStatus(opt.key)}
                           className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 text-sm font-medium">
                        <opt.icon size={14} /> {opt.label}
                      </div>
                      {opt.key === currentStatus && (
                        <div className="text-[11px] text-slate-500">current</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <label className="text-xs text-slate-500 flex flex-col space-y-1">
            <span>Effective Date <span className="text-red-500">*</span></span>
            <input type="date" required
                   data-testid="svc-status-effective-date"
                   value={effectiveDate}
                   onChange={(e) => setEffectiveDate(e.target.value)}
                   className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
          </label>

          <label className="text-xs text-slate-500 flex flex-col space-y-1">
            <span>Reason {CLOSED.has(status) && <span className="text-red-500">*</span>}</span>
            <input type="text" placeholder="Why is the service moving to this status?"
                   data-testid="svc-status-reason"
                   value={reason}
                   onChange={(e) => setReason(e.target.value)}
                   className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
          </label>

          <label className="text-xs text-slate-500 flex flex-col space-y-1">
            <span>Remarks (optional)</span>
            <textarea rows={3}
                      data-testid="svc-status-remarks"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Any additional context…"
                      className="border border-slate-200 rounded-md px-3 py-2 text-sm resize-none" />
          </label>

          {CLOSED.has(status) && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2"
                 data-testid="svc-status-closure-warning">
              Closing service will make Daily Activities, Interviews and Offers read-only for this candidate.
              Historical records remain unchanged and searchable. This action is audit-logged.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose}
                    data-testid="svc-status-cancel"
                    className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
            <button type="submit" disabled={busy}
                    data-testid="svc-status-submit"
                    className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50">
              {busy ? "Saving…" : "Change Status"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
